import { useMutation } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

/**
 * Interface for signup payload
 */
export interface SignUpPayload {
  email: string;
  objective: 'assessing' | 'prescreening' | 'evaluating' | 'validating';
  password1: string;
  password2: string;
  referral?: string;
}

/**
 * Interface for signup response
 */
export interface SignUpResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    email: string;
    phone_number: string | null;
    first_name: string | null;
    last_name: string | null;
    date_joined: string;
    country_code: string;
    date_of_birth: string | null;
    display_photo: string | null;
    address: string | null;
    user_type: string;
    objective: string;
    referral: string | null;
  };
}

/**
 * Hook for signing up using TanStack React Query
 */
export const useSignUp = (
  setSuccess: (value: boolean) => void,
  setClear: (value: ((prev: boolean) => boolean) | boolean) => void,
  setEmail: (value: string) => void
) => {
  const { openToast } = useToast();

  return useMutation({
    mutationFn: async (payload: SignUpPayload) => {
      const response = await apiClient.post<SignUpResponse>('/auth/signup/', payload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      setSuccess(true);
      setClear((prev) => !prev);
      setEmail(variables.email);
      openToast({
        show: true,
        heading: 'Signup successful!',
        text: 'Please check your email for the next steps',
        type: 'success',
        timeOut: 4000,
      });
    },
    onError: (err: any) => {
      console.error('Sign Up Error:', err); // Log error for debugging
      const errData = err?.response?.data;
      const otherError = errData?.non_field_errors ? errData.non_field_errors[0] : undefined;
      const emailError = errData?.email ? errData.email[0] : undefined;
      const detailError = errData?.detail ? errData.detail[0] : undefined;
      const errorMessage =
        errData?.error?.message ??
        emailError ??
        detailError ??
        otherError ??
        'Failed to create your account, please try again later';
      openToast({
        show: true,
        heading: 'Sorry',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    },
  });
};